import { useQuery } from "@tanstack/react-query";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logEducatorDashboard, LogLevel } from "utils";

// Loads the quick groups for a classroom. GET /quick-groups/classrooms/{id}
// returns { used, total, quick_groups: [{ id, code, standard_code, standard_name,
// standard_description, session_length, question_count, last_updated }] }.
// Used both to render the Quick Groups section and to gate the Groups empty
// state (a class with quick groups but no lessons/bundles is not "empty").
//
// Cached so a return navigation renders instantly, but stale immediately: quick
// groups are created and edited on other pages that don't invalidate this key,
// so every mount revalidates in the background. `loading` is true only while
// the FIRST load for a class is in flight — the window the empty-state gate
// cares about.
export const useClassroomQuickGroups = (classroomId) => {
  const { data, dataUpdatedAt, isInitialLoading, error, refetch } = useQuery({
    queryKey: ["quick-groups", classroomId],
    queryFn: async () => {
      try {
        return await request(`quick-groups/classrooms/${classroomId}`, { method: "GET" });
      } catch (e) {
        logEducatorDashboard("useClassroomQuickGroups: failed to load quick groups", {
          level: LogLevel.WARNING,
          event: "quick_groups_list_error",
          statusCode: e?.status,
          classroomId,
        });
        throw e;
      }
    },
    enabled: Boolean(classroomId),
    staleTime: 0,
  });

  // On error the list reads as empty (matching the pre-cache behavior) rather
  // than serving a stale success alongside the error.
  return {
    quickGroups: (error ? null : data?.quick_groups) || [],
    used: error ? 0 : data?.used ?? 0,
    total: error ? 0 : data?.total ?? 0,
    loading: isInitialLoading,
    error: error || null,
    refetch,
    // When the list was last read.
    updatedAt: dataUpdatedAt,
  };
};
